import React, { useState } from 'react';
import { generateSupplierEmail } from '../services/geminiService';
import { Send, Copy, Sparkles } from 'lucide-react';

const EmailGenerator: React.FC = () => {
  const [supplierName, setSupplierName] = useState('');
  const [tone, setTone] = useState<'formal' | 'friendly'>('formal');
  const [loading, setLoading] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState('');

  const handleGenerate = async () => {
    if (!supplierName) return;
    setLoading(true);
    try {
      const text = await generateSupplierEmail(supplierName, tone);
      setGeneratedEmail(text);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedEmail);
    alert('Email vágólapra másolva!');
  };

  return (
    <div
      className="max-w-3xl mx-auto p-6 space-y-6 min-h-screen"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      <div className="bg-card rounded-xl shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          1. Stratégia: A Kapcsolatfelvétel
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Kérd el a beszállítótól a digitális terméklistát (Excel/CSV). A Gemini segít megírni a
          tökéletes levelet, amire nem mondhatnak nemet.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Beszállító neve
            </label>
            <input
              type="text"
              className="w-full p-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-blue-500 dark:focus:border-blue-400 outline-none transition placeholder-gray-400 dark:placeholder-gray-500"
              placeholder="pl. Nagyker Kft."
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hangnem
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setTone('formal')}
                className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition ${
                  tone === 'formal'
                    ? 'bg-slate-800 dark:bg-gray-700 text-white border-slate-800 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-600'
                }`}
              >
                Hivatalos
              </button>
              <button
                onClick={() => setTone('friendly')}
                className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition ${
                  tone === 'friendly'
                    ? 'bg-blue-600 dark:bg-blue-500 text-white border-blue-600 dark:border-blue-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-600'
                }`}
              >
                Közvetlen
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={!supplierName || loading}
          className="w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <span className="animate-pulse">Generálás...</span>
          ) : (
            <>
              <Sparkles size={18} />
              Email Generálása
            </>
          )}
        </button>
      </div>

      {generatedEmail && (
        <div className="bg-card rounded-xl shadow-sm overflow-hidden animate-fade-in">
          <div className="bg-slate-50 dark:bg-gray-900/50 border-b border-primary px-6 py-4 flex justify-between items-center">
            <h3 className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Send size={16} className="text-blue-500 dark:text-blue-400" />
              Generált Email Minta
            </h3>
            <button
              onClick={copyToClipboard}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-1 rounded transition"
            >
              <Copy size={14} />
              Másolás
            </button>
          </div>
          <div className="p-6 bg-slate-50/50 dark:bg-gray-900/50">
            <textarea
              readOnly
              className="w-full h-64 p-4 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 font-mono text-sm resize-none focus:outline-none focus:ring-1 focus:ring-accent"
              value={generatedEmail}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailGenerator;
